import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useRouter } from "next/router";
import { useCallback } from "react";
import useSWRMutation from "swr/mutation";
import type { NskResponse } from "~/models/Api";
import type { DealReportImage } from "~/models/DealReportImage";
import { isSuccess } from "~/utils/api/api_handler";
import { getReportImageSwrKey } from "~/utils/DealReportImageUtil";

type Props = {
	onClose: () => void;
	onDelete: (reportImage: DealReportImage) => Promise<NskResponse>;
	reportImage: DealReportImage;
};

export function ReportImageDeleteConfirmModal({
	onClose,
	onDelete,
	reportImage
}: Props) {
	const { notify } = useNotification();
	const router = useRouter();
	const { trigger, isMutating } = useSWRMutation(
		getReportImageSwrKey(router.asPath, reportImage.dealId),
		async (_key: string, { arg }: { arg: DealReportImage }) => {
			return await onDelete(arg);
		}
	);

	const onDeleteConfirm = useCallback(async () => {
		const response = await trigger(reportImage);
		if (isSuccess(response)) {
			notify({ content: "画像を削除しました。", variant: "success" });
			onClose();
		} else {
			notify({
				content: `エラーが発生しました。 ${response.message ?? ""}`,
				variant: "error"
			});
		}
	}, [notify, onClose, reportImage, trigger]);

	return (
		<Modalv2 isOpen onClose={onClose} size="s">
			<ModalHeader title="画像の削除" />
			<ModalBody>
				画像を削除します。
				<Alert mt={designTokens["spacing/m"]} variant="warning">
					この操作は元に戻せません。
				</Alert>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button disabled={isMutating} onClick={onClose} variant="secondary">
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						loading={isMutating}
						onClick={onDeleteConfirm}
						variant="danger"
					>
						削除
					</Button>
				}
			/>
		</Modalv2>
	);
}
